import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.addEventListener('click', this.#onCartSupplyListItemClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    this.removeEventListener('click', this.#onCartSupplyListItemClick);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const remove = () => cartItemRowToRemove.remove();

    if (prefersReducedMotion()) return remove();

    // Add class to the row to trigger the animation
    cartItemRowToRemove.style.setProperty('--row-height', `${cartItemRowToRemove.clientHeight}px`);
    cartItemRowToRemove.classList.add('removing');

    // Remove the row after the animation ends
    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
 * Handles click on cart supply list item.
 * @param {MouseEvent} event
 */
  #onCartSupplyListItemClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const supplyItem = target.closest('.cart_supply_list_item');
  
    if (!supplyItem) return;
  
    const variantId = supplyItem.getAttribute('data-variant-id');
    const sellingPlanId = supplyItem.getAttribute('data-selling-id');
  
    if (!variantId) {
      console.error('Missing variant ID on supply list item');
      return;
    }
  
    const cartItemRow = supplyItem.closest('tr');
    if (!cartItemRow) return;
  
    const lineIndex = this.refs.cartItemRows.indexOf(cartItemRow);
    if (lineIndex === -1) {
      console.error('Cart item row not found in refs');
      return;
    }
  
    this.#removeLineItem(lineIndex + 1)
      .then(() => this.#addNewItem(variantId, sellingPlanId))
      .then(() => {
        sectionRenderer.renderSection(this.sectionId, { cache: false });
      })
      .catch((err) => console.error('Error processing cart supply click:', err));
  };
  
  #removeLineItem(line) {
    return new Promise((resolve, reject) => {
      this.updateQuantity({
        line,
        quantity: 0,
        action: 'clear',
      });
  
      const onCartUpdate = (event) => {
        if (event.detail.source === 'cart-items-component') {
          document.removeEventListener(ThemeEvents.cartUpdate, onCartUpdate);
          resolve();
        }
      };
  
      document.addEventListener(ThemeEvents.cartUpdate, onCartUpdate);
  
      setTimeout(() => {
        document.removeEventListener(ThemeEvents.cartUpdate, onCartUpdate);
        resolve();
      }, 3000);
    });
  }
  
  #addNewItem(variantId, sellingPlanId) {
    const sectionsToUpdate = new Set([this.sectionId]);
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
  
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
  
    const body = {
      id: parseInt(variantId, 10),
      quantity: 1,
      sections: Array.from(sectionsToUpdate).join(','),
    };
  
    if (sellingPlanId) {
      body.selling_plan = sellingPlanId;
    }
  
    return fetch(`${Theme.routes.cart_add_url}`, fetchConfig('json', { body: JSON.stringify(body) }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponse = JSON.parse(responseText);
        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponse.sections?.[this.sectionId] || '',
          'text/html'
        );
  
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;
  
        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponse.sections,
          })
        );
  
        morphSection(this.sectionId, parsedResponse.sections?.[this.sectionId] || '');
  
        return parsedResponse;
      });
  }


  
}

// Add this after the existing click event listeners (around line 330)

// Handle cart addon product clicks
document.addEventListener('click', function (e) {
  const addonProduct = e.target.closest('.cart_addon_product_product');
  if (!addonProduct) return;

  const variantId = addonProduct.getAttribute('variant__id');
  const productId = addonProduct.getAttribute('product__id');

  if (!variantId) {
    console.error('Missing variant ID on addon product');
    return;
  }

  addonProduct.classList.add('active');

  // Get all cart-items-component instances to update
  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  const sectionsToUpdate = new Set();
  
  cartItemsComponents.forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionsToUpdate.add(item.dataset.sectionId);
    }
  });

  // Prepare the request body
  const body = JSON.stringify({
    id: parseInt(variantId, 10),
    quantity: 1,
    sections: Array.from(sectionsToUpdate).join(','),
  });

  // Add visual feedback
  addonProduct.style.opacity = '0.5';
  addonProduct.style.pointerEvents = 'none';

  // Make the add to cart request
  fetch(`${Theme.routes.cart_add_url}`, fetchConfig('json', { body }))
    .then((response) => response.text())
    .then((responseText) => {
      const parsedResponse = JSON.parse(responseText);

      if (parsedResponse.status === 422 || parsedResponse.description) {
        console.error('Error adding product:', parsedResponse.description || parsedResponse.message);
        alert(parsedResponse.description || 'Unable to add product to cart');
        return;
      }

      // Get the new item count
      const firstSectionId = Array.from(sectionsToUpdate)[0];
      if (parsedResponse.sections && parsedResponse.sections[firstSectionId]) {
        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponse.sections[firstSectionId],
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Dispatch cart update event to update all cart sections
        document.dispatchEvent(
          new CartAddEvent({}, firstSectionId, {
            itemCount: newCartItemCount,
            source: 'cart-addon-product',
            sections: parsedResponse.sections,
          })
        );

        // Update all cart sections
        sectionsToUpdate.forEach((sectionId) => {
          if (parsedResponse.sections[sectionId]) {
            morphSection(sectionId, parsedResponse.sections[sectionId]);
          }
        });
      }
    })
    .catch((error) => {
      console.error('Error adding addon product:', error);
      alert('Unable to add product to cart. Please try again.');
    })
    .finally(() => {
      // Remove visual feedback
      addonProduct.style.opacity = '1';
      addonProduct.style.pointerEvents = 'auto';
    });
});

document.addEventListener('click', function (e) {
  const supplySelect = e.target.closest('.cart_supply_select');
  if (!supplySelect) return;

  const supplyList = supplySelect.nextElementSibling;
  if (!supplyList?.classList.contains('cart_supply_list')) return;

  const isVisible = supplyList.style.display === "block";
  supplyList.style.display = isVisible ? "none" : "block";
  supplySelect.classList.toggle("active", !isVisible);
});

document.addEventListener('click', function (e) {
  const clickedInside = e.target.closest('.cart_supply_select, .cart_supply_list');
  if (!clickedInside) {
    document.querySelectorAll('.cart_supply_list').forEach(list => list.style.display = 'none');
    document.querySelectorAll('.cart_supply_select').forEach(select => select.classList.remove('active'));
  }
});

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
